/**
 * What a viewer actually played (Phase 9), for the owner's analytics: the
 * stretches between successive looks at a playing video, joined where they
 * run on, sent in batches. A seek, a pause or a jump back starts a new
 * stretch, so skipping ahead never counts what was skipped.
 */

/** A look further on than this from the last is a seek, not playback. */
export const SEEK_MS = 1500

export class PlayedRanges {
  private ranges: Array<[number, number]> = []
  private last: number | null = null

  /** The playhead at `ms`; `playing` false forgets where it was. */
  observe(ms: number, playing: boolean): void {
    if (!playing || !Number.isFinite(ms)) {
      this.last = null
      return
    }
    const last = this.last
    if (last !== null && ms > last && ms - last <= SEEK_MS) this.add(last, ms)
    this.last = ms
  }

  private add(s: number, e: number): void {
    const tail = this.ranges[this.ranges.length - 1]
    if (tail && s >= tail[0] && s <= tail[1] + 50) tail[1] = Math.max(tail[1], e)
    else this.ranges.push([s, e])
  }

  get pending(): boolean {
    return this.ranges.length > 0
  }

  /** The stretches so far, in whole ms, and a clean slate. */
  take(): Array<[number, number]> {
    const out = this.ranges.map(([s, e]) => [Math.round(s), Math.round(e)] as [number, number])
    this.ranges = []
    return out
  }
}

/** The random id this browser keeps so a returning viewer is one viewer; null without storage. */
export function viewerId(storage: Pick<Storage, 'getItem' | 'setItem'> | null): string {
  const fresh = (): string =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  if (!storage) return fresh()
  try {
    const known = storage.getItem('ls-viewer')
    if (known && /^[\w-]{8,64}$/.test(known)) return known
    const id = fresh()
    storage.setItem('ls-viewer', id)
    return id
  } catch {
    return fresh()
  }
}
